import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  avatarUrl?: string;
  phone?: string;
  balance?: number;
  createdAt?: number;
  updatedAt?: number;
  lastLogin?: number;
}

export interface CreateUserData {
  email: string;
  name: string;
  password: string;
  role: string;
  phone?: string;
}

export interface UpdateUserData {
  name?: string;
  role?: string;
  isActive?: boolean;
  phone?: string;
  avatarUrl?: string;
}

export const userService = {
  async getUsers(): Promise<User[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/users`);
    return response.data || [];
  },

  async getUser(id: string): Promise<User> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/users/${id}`);
    return response.data;
  },

  async createUser(data: CreateUserData): Promise<User> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async updateUser(id: string, data: UpdateUserData): Promise<User> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
    });
  },

  async toggleUserStatus(id: string, isActive: boolean): Promise<User> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/users/${id}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ isActive }),
      }
    );
    return response.data;
  },

  async assignRole(id: string, role: string): Promise<User> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/users/${id}/role`,
      {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }
    );
    return response.data;
  },

  async getUsersByRole(role: string): Promise<User[]> {
    const response = await authService.fetchWithAuth(
      `${API_URL}/api/users?role=${role}`
    );
    return response.data || [];
  },

  async getUserStats(): Promise<{
    total: number;
    byRole: Record<string, number>;
    active: number;
    inactive: number;
  }> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/users/stats`);
    return response.data;
  },
};